/**
 * McpKeyList - List and manage MCP API keys
 *
 * Features:
 * - DataTable with name, permission, status, last used, actions
 * - Create button triggering modal
 * - Revoke action with confirmation
 * - Empty state
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Button,
  Badge,
  DataTable,
  Column,
  Modal,
  Input,
  Label,
  Spinner,
} from '@/components/ui';
import {
  Plus,
  Bot,
  Trash2,
  Clock,
  AlertCircle,
  Eye,
  Play,
  ShieldCheck,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import {
  listMcpKeys,
  revokeMcpKey,
  McpKeyListItem,
  McpPermissionGroup,
} from '@/services/mcpKeyService';
import { MCP_PERMISSION_GROUPS } from '@simcheck/shared-types';
import { useUserTimezone } from '@/hooks/useDateFormat';

interface McpKeyListProps {
  organizationId: string;
  onCreateClick: () => void;
}

// Format relative time
function formatRelativeTime(date: Date | string | undefined, timeZone: string): string {
  if (!date) return 'Never';
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 2592000000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone });
}

// Permission group badge config
const permissionConfig: Record<McpPermissionGroup, { icon: React.ReactNode; className: string }> = {
  read_only: {
    icon: <Eye className="w-3 h-3 mr-1" />,
    className: 'bg-muted text-muted-foreground',
  },
  execute_flows: {
    icon: <Play className="w-3 h-3 mr-1" />,
    className: 'bg-tech-4g-bg text-tech-4g',
  },
  full_access: {
    icon: <ShieldCheck className="w-3 h-3 mr-1" />,
    className: 'bg-status-warning-bg text-status-warning',
  },
};

export function McpKeyList({ organizationId, onCreateClick }: McpKeyListProps) {
  const { currentUser } = useAuth();
  const { success, error: showError } = useToast();
  const timeZone = useUserTimezone();

  // Data state
  const [mcpKeys, setMcpKeys] = useState<McpKeyListItem[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<McpKeyListItem | null>(null);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  // Fetch MCP keys
  const fetchMcpKeys = useCallback(async () => {
    if (!organizationId) return;

    try {
      const response = await listMcpKeys(organizationId);
      setMcpKeys(response.mcpKeys || []);
    } catch (err: any) {
      console.error('Error fetching MCP keys:', err);
      showError(err.message || 'Failed to load MCP keys');
    } finally {
      setLoading(false);
    }
  }, [organizationId, showError]);

  useEffect(() => {
    fetchMcpKeys();
  }, [fetchMcpKeys]);

  // Handle revoke
  const handleRevoke = async () => {
    if (!selectedKey) return;

    setRevoking(true);
    try {
      await revokeMcpKey(selectedKey.id, organizationId, revokeReason || undefined);
      success('MCP key revoked successfully');
      setShowRevokeModal(false);
      setSelectedKey(null);
      setRevokeReason('');
      fetchMcpKeys();
    } catch (err: any) {
      showError(err.message || 'Failed to revoke MCP key');
    } finally {
      setRevoking(false);
    }
  };

  // Get permission group display info
  const getPermissionGroupInfo = (group: McpPermissionGroup | undefined) => {
    if (!group) return { displayName: 'Unknown', description: '' };
    const found = MCP_PERMISSION_GROUPS.find((g) => g.id === group);
    return found || { displayName: group, description: '' };
  };

  // Table columns
  const columns: Column<McpKeyListItem>[] = [
    {
      key: 'name',
      header: 'Key',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
            <Bot className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="font-semibold">{row.name}</div>
            <div className="text-xs text-muted-foreground font-mono">{row.keyPrefix}...</div>
          </div>
        </div>
      ),
    },
    {
      key: 'mcpPermissionGroup',
      header: 'Permission',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => {
        const group = row.mcpPermissionGroup;
        const info = getPermissionGroupInfo(group);
        const config = group ? permissionConfig[group] : permissionConfig.read_only;

        return (
          <Badge className={config.className}>
            {config.icon}
            {info.displayName}
          </Badge>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <Badge
          className={
            row.status === 'active'
              ? 'bg-status-success-bg text-status-success'
              : row.status === 'revoked'
              ? 'bg-status-error-bg text-status-error'
              : 'bg-status-warning-bg text-status-warning'
          }
        >
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'lastUsedAt',
      header: 'Last Used',
      sortable: true,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex items-center gap-1 text-muted-foreground">
          <Clock className="w-3 h-3" />
          <span className="text-sm">{formatRelativeTime(row.lastUsedAt, timeZone)}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      sortable: false,
      render: (_value: unknown, row: McpKeyListItem) => (
        <div className="flex gap-2 justify-end">
          {row.status === 'active' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedKey(row);
                setShowRevokeModal(true);
              }}
              className="text-status-error hover:text-status-error"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <>
      {mcpKeys.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/30">
          <Bot className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No MCP Keys</h3>
          <p className="text-muted-foreground mb-4">
            Create an MCP key to connect AI assistants like Claude Desktop or Cursor
          </p>
          <Button onClick={onCreateClick}>
            <Plus className="w-4 h-4 mr-2" />
            Create MCP Key
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={onCreateClick} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Create MCP Key
            </Button>
          </div>
          <DataTable
            columns={columns}
            data={mcpKeys}
            keyExtractor={(row) => row.id}
            paginated
            defaultPageSize={5}
          />
        </div>
      )}

      {/* Revoke Modal */}
      <Modal
        isOpen={showRevokeModal}
        onClose={() => {
          setShowRevokeModal(false);
          setSelectedKey(null);
          setRevokeReason('');
        }}
        title="Revoke MCP Key"
        description={`Are you sure you want to revoke "${selectedKey?.name}"? This action cannot be undone.`}
        size="md"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setShowRevokeModal(false);
                setSelectedKey(null);
                setRevokeReason('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRevoke}
              disabled={revoking}
            >
              {revoking && <Spinner size="sm" className="mr-2" />}
              Revoke Key
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="bg-status-error-bg/30 border border-status-error/30 rounded-lg p-4">
            <div className="flex items-center gap-2 text-status-error mb-2">
              <AlertCircle className="w-5 h-5" />
              <span className="font-semibold">Warning</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Any AI assistants using this key will immediately lose access.
              This action cannot be undone.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="revokeReason">Reason (optional)</Label>
            <Input
              id="revokeReason"
              placeholder="Why is this key being revoked?"
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}

export default McpKeyList;
